import { useCallback, useState } from "react";
import type React from "react";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import {
  deleteChatbotSession,
  getChatbotSessions,
  getChatbotSessionTurns,
  type ChatbotReferenceImage,
  type ChatbotSessionSummary,
} from "../../services/chatbot.service";
import { createTurnMessages, createWelcomeMessage } from "./chatbotMessageUtils";
import { SESSION_LIST_PAGE, SESSION_LIST_PAGE_SIZE } from "./chatbotHook.constants";
import type { WidgetMessage } from "./types";

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface UseChatbotSessionsOptions {
  sessionId: string | null;
  setSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  setMessages: React.Dispatch<React.SetStateAction<WidgetMessage[]>>;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  setReferenceImages: React.Dispatch<React.SetStateAction<ChatbotReferenceImage[]>>;
  setReferenceImageUploadError: React.Dispatch<React.SetStateAction<string | null>>;
  isStreaming: boolean;
  t: Translate;
}

export interface UseChatbotSessionsReturn {
  sessions: ChatbotSessionSummary[];
  isSessionListOpen: boolean;
  isLoadingSessions: boolean;
  isLoadingSessionTurns: boolean;
  setIsSessionListOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loadSessions: () => Promise<void>;
  refreshSessionTurns: (targetSessionId?: string | null) => Promise<void>;
  handleOpenSessions: () => void;
  handleToggleSessions: () => void;
  handleSelectSession: (selectedSessionId: string) => Promise<void>;
  handleDeleteSession: (selectedSessionId: string) => Promise<void>;
}

export const useChatbotSessions = ({
  sessionId,
  setSessionId,
  setMessages,
  setInput,
  setReferenceImages,
  setReferenceImageUploadError,
  isStreaming,
  t,
}: UseChatbotSessionsOptions): UseChatbotSessionsReturn => {
  const [sessions, setSessions] = useState<ChatbotSessionSummary[]>([]);
  const [isSessionListOpen, setIsSessionListOpen] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingSessionTurns, setIsLoadingSessionTurns] = useState(false);

  const loadSessions = useCallback(async (): Promise<void> => {
    setIsLoadingSessions(true);
    try {
      const response = await getChatbotSessions(SESSION_LIST_PAGE, SESSION_LIST_PAGE_SIZE);
      setSessions(response.data);
    } catch {
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  const refreshSessionTurns = useCallback(async (targetSessionId?: string | null): Promise<void> => {
    const resolvedSessionId = targetSessionId || sessionId;
    if (!resolvedSessionId) return;

    const { turns } = await getChatbotSessionTurns(resolvedSessionId);
    const nextMessages = createTurnMessages(resolvedSessionId, turns);
    setSessionId(resolvedSessionId);
    setMessages(nextMessages.length ? nextMessages : [createWelcomeMessage()]);
  }, [sessionId, setMessages, setSessionId]);

  const handleOpenSessions = (): void => {
    setIsSessionListOpen((isOpen) => {
      if (!isOpen) void loadSessions();
      return true;
    });
  };

  const handleToggleSessions = (): void => {
    setIsSessionListOpen((isOpen) => {
      const nextIsOpen = !isOpen;
      if (nextIsOpen) void loadSessions();
      return nextIsOpen;
    });
  };

  const handleSelectSession = async (selectedSessionId: string): Promise<void> => {
    if (isLoadingSessionTurns || isStreaming) return;

    setIsLoadingSessionTurns(true);
    try {
      await refreshSessionTurns(selectedSessionId);
      setInput("");
      setReferenceImages([]);
      setReferenceImageUploadError(null);
      setIsSessionListOpen(false);
    } catch {
      setIsSessionListOpen(false);
    } finally {
      setIsLoadingSessionTurns(false);
    }
  };

  const handleDeleteSession = async (selectedSessionId: string): Promise<void> => {
    if (!selectedSessionId || isLoadingSessionTurns || isStreaming) return;

    const result = await Swal.fire({
      title: t("msg_are_you_sure"),
      text: t("btn_delete_text"),
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("btn_delete"),
      cancelButtonText: t("btn_cancel"),
      reverseButtons: true,
      customClass: {
        popup: "swal-restaurant",
        confirmButton: "btn btn-danger col",
        cancelButton: "btn btn-secondary col",
      },
      allowOutsideClick: false,
    } as any);

    if (!result.isConfirmed) return;

    try {
      await deleteChatbotSession(selectedSessionId);
      setSessions((current) => current.filter((session) => session._id !== selectedSessionId));

      if (sessionId === selectedSessionId) {
        setSessionId(null);
        setInput("");
        setReferenceImages([]);
        setReferenceImageUploadError(null);
        setMessages([createWelcomeMessage()]);
      }
    } catch (error) {
      void Swal.fire({
        icon: "error",
        text: (error as Error)?.message || t("chatbot_error_message"),
        ...SWAL_SETTINGS,
      } as any);
    }
  };

  return {
    sessions,
    isSessionListOpen,
    isLoadingSessions,
    isLoadingSessionTurns,
    setIsSessionListOpen,
    loadSessions,
    refreshSessionTurns,
    handleOpenSessions,
    handleToggleSessions,
    handleSelectSession,
    handleDeleteSession,
  };
};
